/**
 * Run something at most once per window, and GUARANTEE it runs.
 *
 * Written after avg-latency read the first attempt and found it inverted — the fault
 * is worth recording, because the code looked like a coalescer and the comment above
 * it claimed to be one:
 *
 *   if (pending) clearTimeout(pending)                 // ← trailing debounce
 *   pending = setTimeout(run, 250)
 *
 * That is reset-on-every-poke, which fails in BOTH directions at once:
 *
 *   · pokes faster than the window  → the timer is reset before it can fire, so it
 *     NEVER fires. The thing it was protecting starves for as long as the burst
 *     lasts — the opposite of the "too many requests" it was written to prevent.
 *   · pokes slower than the window  → every poke gets its own timer and fires, so
 *     nothing is coalesced at all. The saving only exists during a burst, and a
 *     burst is exactly when it does not fire.
 *
 * So the debounce did nothing in the quiet case and did harm in the loud one. What
 * was wanted is a fixed window: the FIRST poke opens it, later pokes inside it are
 * absorbed, and it closes by running. Rate-capped and always progressing.
 *
 * The caller gets a plain `poke()`; timers are the module's business, not theirs.
 */
export function atMostEvery(ms: number, run: () => void): () => void {
  let pending: number | null = null
  return () => {
    // Already inside a window — this poke is absorbed, and the run that is coming
    // will account for it. NOT clearTimeout: that is the bug above.
    if (pending !== null) return
    pending = window.setTimeout(() => {
      pending = null
      run()
    }, ms)
  }
}
